import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Star, Users, ThumbsUp, Phone, ExternalLink, X, Clock } from 'lucide-react';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop } from '../components/crm/reportCards';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { db } from '../firebase.js';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { getAccountById, findAccountIdByEmail } from '../services/firestoreService';
import {
  normEmail,
  pickProfilePicUrl,
  accountDisplayForCheckIn,
  checkInDateToJs,
  initialsFromName,
  CheckInStarTick,
  CheckInRatingPanel,
  CheckInPendingPanel,
} from '../components/crm/CheckInInsights';
import {
  GLASS_TONE,
  GLASS_TEXT,
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GLASS_MODAL_OVERLAY,
  GLASS_MODAL_PANEL,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassBadge,
  GlassAccentButton,
  GlassEmpty,
  GlassSearch,
  GlassSelect,
  SlidingPills,
  glassChartTheme,
} from '../components/glass/GlassUi';

const RESPONSE_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'responded', label: 'Responded' },
  { value: 'noResponse', label: 'Outstanding' },
];

// Resolve the selected timeframe to a [start, end) window; null means no bound on that side.
function timeRangeToWindow(timeRange, monthPick, customStart, customEnd) {
  const now = new Date();
  switch (timeRange) {
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return { start, end };
    }
    case 'month': {
      const [y, m] = String(monthPick || '').split('-').map(Number);
      if (!y || !m) return null;
      return { start: new Date(y, m - 1, 1), end: new Date(y, m, 1) };
    }
    case 'last3Months':
    case 'last6Months':
    case 'lastYear': {
      const months = { last3Months: 3, last6Months: 6, lastYear: 12 }[timeRange];
      const start = new Date(now);
      start.setMonth(start.getMonth() - months);
      return { start, end: null };
    }
    case 'custom': {
      const start = customStart ? new Date(`${customStart}T00:00:00`) : null;
      // End date is inclusive, so bound at the start of the following day.
      const end = customEnd ? new Date(new Date(`${customEnd}T00:00:00`).getTime() + 24 * 60 * 60 * 1000) : null;
      if (!start && !end) return null;
      return { start, end };
    }
    default:
      return null; // allTime
  }
}

const currentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// The "YYYY-MM" bucket a row belongs to, matching the dashboard's periodKey. Prefer the
// authored periodKey; otherwise derive from the date's UTC month (check-in scheduledDates are
// authored at UTC midnight, so local-time bucketing would shift a 1st-of-month send into the
// prior month).
function rowPeriodKey(r) {
  if (r.periodKey) return r.periodKey;
  if (!r.date) return null;
  return `${r.date.getUTCFullYear()}-${String(r.date.getUTCMonth() + 1).padStart(2, '0')}`;
}

// A check-in counts as "responded" once it carries a rating or a resolved status.
// Mirrors the shared definition in ClientOverview / the check-in cloud functions.
function isCheckInResponded(data) {
  const num = Number(data?.overallSatisfaction ?? data?.rating);
  const hasNumeric = Number.isFinite(num) && num >= 1 && num <= 5;
  const statusOk =
    typeof data?.overallStatus === 'string' &&
    ['Good', 'Okay', 'Not Good'].includes(data.overallStatus);
  return hasNumeric || data?.status === 'completed' || statusOk;
}

// Client avatar + company + contact, matching the dashboard hover panels.
function ClientCell({ row, size = 'h-10 w-10' }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      {row.imageUrl ? (
        <img src={row.imageUrl} alt="" className={`${size} rounded-full object-cover shrink-0 border border-black/10 dark:border-white/[0.15]`} />
      ) : (
        <div className={`${size} rounded-full shrink-0 flex items-center justify-center text-xs font-semibold bg-[#FDD5D0] text-[#1C1916]`}>
          {initialsFromName(row.companyName)}
        </div>
      )}
      <div className="min-w-0">
        <div className="font-medium text-gray-900 dark:text-white truncate">{row.companyName}</div>
        {row.contactName && row.contactName !== row.companyName ? (
          <div className="text-xs text-gray-500 dark:text-white/40 truncate">{row.contactName}</div>
        ) : null}
      </div>
    </div>
  );
}

function StarRow({ rating, starClass = 'text-lg' }) {
  return (
    <div className="flex space-x-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`${starClass} ${rating >= star ? 'text-[#FFB547]' : 'text-black/[0.12] dark:text-white/20'}`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

const ClientSatisfaction = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [timeRange, setTimeRange] = useState('thisMonth');
  const [monthPick, setMonthPick] = useState(currentMonthValue);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [responseFilter, setResponseFilter] = useState('all'); // all | responded | noResponse
  const [searchQuery, setSearchQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);

  // Hover popover for the rating bars (same behavior as the CRM dashboard card).
  const [chartHover, setChartHover] = useState(null); // { row }
  const chartHoverTimer = useRef(null);
  const openChartHover = (row) => {
    if (chartHoverTimer.current) clearTimeout(chartHoverTimer.current);
    setChartHover({ row });
  };
  const scheduleCloseChartHover = () => {
    if (chartHoverTimer.current) clearTimeout(chartHoverTimer.current);
    chartHoverTimer.current = setTimeout(() => setChartHover(null), 250);
  };

  // Hover popover for the "No Response" tile — lists clients who haven't responded.
  const [noResponseHoverOpen, setNoResponseHoverOpen] = useState(false);
  const noResponseHoverTimer = useRef(null);
  const openNoResponseHover = () => {
    if (noResponseHoverTimer.current) clearTimeout(noResponseHoverTimer.current);
    setNoResponseHoverOpen(true);
  };
  const scheduleCloseNoResponseHover = () => {
    if (noResponseHoverTimer.current) clearTimeout(noResponseHoverTimer.current);
    noResponseHoverTimer.current = setTimeout(() => setNoResponseHoverOpen(false), 250);
  };

  useEffect(() => () => {
    if (chartHoverTimer.current) clearTimeout(chartHoverTimer.current);
    if (noResponseHoverTimer.current) clearTimeout(noResponseHoverTimer.current);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      try {
        // Survey responses live in surveyResults; check_ins carries the account link
        // (accountId / userEmail) plus pending state; scheduled_check_ins holds legacy /
        // orphaned outstanding sends that never got a check_ins doc. Merge all three so
        // the report shows every response AND everyone who hasn't gotten to it yet.
        const [resultsSnap, checkInsSnap, scheduledSnap] = await Promise.all([
          getDocs(collection(db, 'surveyResults')),
          getDocs(collection(db, 'check_ins')),
          getDocs(collection(db, 'scheduled_check_ins')),
        ]);

        const rowsByKey = new Map();
        // Track which check_ins docs we've seen so scheduled_check_ins synthesis can
        // skip any outstanding send that already has a check_ins row.
        const seenCheckInIds = new Set();

        for (const d of resultsSnap.docs) {
          const data = d.data();
          const num = Number(data.responses?.overall?.rating);
          const row = {
            id: d.id,
            userId: data.userId || null,
            surveyId: data.surveyId || null,
            rating: Number.isFinite(num) && num >= 1 && num <= 5 ? num : null,
            feedback: String(data.responses?.additionalFeedback || '').trim(),
            requestedCall: false,
            responded: true,
            accountId: null,
            userEmail: '',
            periodKey: null,
            date: checkInDateToJs(data.submittedAt),
          };
          const key = row.surveyId && row.userId ? `${row.surveyId}|${row.userId}` : `sr-${d.id}`;
          rowsByKey.set(key, row);
        }

        for (const d of checkInsSnap.docs) {
          const data = d.data();
          seenCheckInIds.add(d.id);
          const num = Number(data.overallSatisfaction ?? data.rating);
          const hasNumeric = Number.isFinite(num) && num >= 1 && num <= 5;
          // Every check_ins doc is either a response or an outstanding send — show them all.
          const isResponded = isCheckInResponded(data);

          const patch = {
            userId: data.userId || null,
            surveyId: data.surveyId || null,
            rating: hasNumeric ? num : null,
            feedback: String(data.comments || data.additionalFeedback || '').trim(),
            requestedCall: data.requestedCheckInCall === true,
            responded: isResponded,
            accountId: data.accountId || null,
            userEmail: String(data.userEmail || '').trim(),
            periodKey: data.periodKey || null,
            date: isResponded
              ? checkInDateToJs(data.submittedAt || data.completedAt || data.createdAt)
              : checkInDateToJs(data.scheduledDate || data.createdAt),
          };
          const key = patch.surveyId && patch.userId ? `${patch.surveyId}|${patch.userId}` : `ci-${d.id}`;
          const existing = rowsByKey.get(key);
          if (existing) {
            rowsByKey.set(key, {
              ...existing,
              rating: existing.rating ?? patch.rating,
              feedback: existing.feedback || patch.feedback,
              requestedCall: existing.requestedCall || patch.requestedCall,
              responded: existing.responded || patch.responded,
              accountId: existing.accountId || patch.accountId,
              userEmail: existing.userEmail || patch.userEmail,
              periodKey: existing.periodKey || patch.periodKey,
              date: existing.date || patch.date,
            });
          } else {
            rowsByKey.set(key, { id: d.id, ...patch });
          }
        }

        // Outstanding sends that live only in scheduled_check_ins (legacy rows, or ones
        // whose check_ins doc was removed). Skip any still-scheduled/cancelled ones and
        // any whose check_ins doc we already loaded above.
        for (const d of scheduledSnap.docs) {
          const data = d.data();
          if (data.status && data.status !== 'sent') continue;
          const checkInId = data.checkInId;
          if (checkInId && seenCheckInIds.has(checkInId)) continue;
          const key = checkInId ? `ci-${checkInId}` : `sched-${d.id}`;
          if (rowsByKey.has(key)) continue;
          const when = checkInDateToJs(data.scheduledDate || data.sentAt || data.createdAt);
          rowsByKey.set(key, {
            id: key,
            userId: null,
            surveyId: data.surveyId || null,
            rating: null,
            feedback: '',
            requestedCall: false,
            responded: false,
            accountId: data.clientId || null,
            userEmail: String(data.clientEmail || '').trim(),
            periodKey: data.periodKey || null,
            date: when,
          });
        }

        // Resolve each response to an account so we can show company, contact, and photo.
        const accountCache = new Map();
        const loadAccount = (id) => {
          if (!id) return Promise.resolve(null);
          if (!accountCache.has(id)) {
            accountCache.set(id, getAccountById(id).catch(() => null));
          }
          return accountCache.get(id);
        };
        const userCache = new Map();
        const loadUser = (uid) => {
          if (!uid) return Promise.resolve(null);
          if (!userCache.has(uid)) {
            userCache.set(
              uid,
              getDoc(doc(db, 'users', uid))
                .then((snap) => (snap.exists() ? snap.data() : null))
                .catch(() => null)
            );
          }
          return userCache.get(uid);
        };
        const emailAccountCache = new Map();
        const findAccountByEmailCached = (email) => {
          const n = normEmail(email);
          if (!n) return Promise.resolve(null);
          if (!emailAccountCache.has(n)) {
            emailAccountCache.set(n, findAccountIdByEmail(email).catch(() => null));
          }
          return emailAccountCache.get(n);
        };

        const resolved = await Promise.all(
          [...rowsByKey.values()].map(async (row) => {
            let account = null;
            let email = row.userEmail;
            let userData = null;

            if (row.accountId) account = await loadAccount(row.accountId);
            if (!account && row.userId) {
              userData = await loadUser(row.userId);
              if (userData?.accountId) account = await loadAccount(userData.accountId);
              if (!email && userData?.email) email = String(userData.email).trim();
            }
            if (!account && email) {
              const accId = await findAccountByEmailCached(email);
              if (accId) account = await loadAccount(accId);
            }
            // Legacy rows sometimes stored the account id in userId directly.
            if (!account && row.userId) account = await loadAccount(row.userId);

            if (account) {
              const disp = accountDisplayForCheckIn(account, email);
              return {
                ...row,
                companyName: disp.name,
                contactName: disp.contactName,
                imageUrl: disp.imageUrl,
                accountId: account.id,
              };
            }
            const userName = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim() || userData?.name || '';
            return {
              ...row,
              companyName: userData?.company || userName || email || 'Unknown Company',
              contactName: userName,
              imageUrl: pickProfilePicUrl(userData),
              accountId: null,
            };
          })
        );

        resolved.sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
        if (!cancelled) setRows(resolved);
      } catch (error) {
        console.error('Error loading survey data:', error);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply the timeframe + response-status + search filters to every card on the page.
  const filteredRows = useMemo(() => {
    let list = rows;
    // Calendar-month presets bucket by the check-in's periodKey (matching the CRM dashboard);
    // rolling/custom windows filter by the actual date.
    if (timeRange === 'thisMonth' || timeRange === 'month') {
      const target = timeRange === 'thisMonth' ? currentMonthValue() : monthPick;
      list = target ? list.filter((r) => rowPeriodKey(r) === target) : list;
    } else {
      const window = timeRangeToWindow(timeRange, monthPick, customStart, customEnd);
      if (window) {
        list = list.filter((r) => {
          if (!r.date) return false;
          const t = r.date.getTime();
          if (window.start && t < window.start.getTime()) return false;
          if (window.end && t >= window.end.getTime()) return false;
          return true;
        });
      }
    }
    if (responseFilter === 'responded') list = list.filter((r) => r.responded);
    if (responseFilter === 'noResponse') list = list.filter((r) => !r.responded);
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        [r.companyName, r.contactName, r.userEmail, r.feedback]
          .some((v) => String(v || '').toLowerCase().includes(q))
      );
    }
    return list;
  }, [rows, timeRange, monthPick, customStart, customEnd, responseFilter, searchQuery]);

  const metrics = useMemo(() => {
    const responded = filteredRows.filter((r) => r.responded);
    const rated = responded.filter((r) => r.rating != null);
    const avg = rated.length ? rated.reduce((sum, r) => sum + r.rating, 0) / rated.length : null;
    const happy = rated.filter((r) => r.rating >= 4).length;
    return {
      avg,
      totalResponses: responded.length,
      noResponse: filteredRows.length - responded.length,
      happyPct: rated.length ? Math.round((happy / rated.length) * 100) : null,
      ratedCount: rated.length,
    };
  }, [filteredRows]);

  // Star distribution for the bar chart, with submitter details for the hover panel.
  const barChartRows = useMemo(() => {
    const buckets = [0, 1, 2, 3, 4].map((i) => ({ stars: `${i + 1}`, count: 0, submitters: [] }));
    for (const r of filteredRows) {
      if (r.rating == null) continue;
      const idx = Math.min(4, Math.max(0, Math.round(r.rating) - 1));
      buckets[idx].count += 1;
      buckets[idx].submitters.push({
        name: r.companyName,
        contactName: r.contactName,
        imageUrl: r.imageUrl,
        accountId: r.accountId,
        comment: r.feedback,
        requestedCall: r.requestedCall,
        date: r.date,
      });
    }
    return buckets;
  }, [filteredRows]);

  // Clients in the current window who haven't responded — powers the No Response hover panel.
  const noResponseSubmitters = useMemo(
    () =>
      filteredRows
        .filter((r) => !r.responded)
        .map((r) => ({
          name: r.companyName,
          contactName: r.contactName,
          imageUrl: r.imageUrl,
          accountId: r.accountId,
          date: r.date,
        })),
    [filteredRows]
  );

  const overviewTiles = [
    {
      label: 'Overall Score',
      value: metrics.avg != null ? `${metrics.avg.toFixed(1)}/5` : '—',
      icon: Star,
    },
    {
      label: 'Total Responses',
      value: metrics.totalResponses,
      icon: Users,
    },
    {
      id: 'noResponse',
      label: 'Outstanding',
      value: metrics.noResponse,
      icon: Clock,
      valueClassName: metrics.noResponse > 0 ? GLASS_TEXT.amber : '',
    },
    {
      label: 'Happy Clients (4★+)',
      value: metrics.happyPct != null ? `${metrics.happyPct}%` : '—',
      icon: ThumbsUp,
      valueClassName: metrics.happyPct != null ? GLASS_TEXT.mint : '',
    },
  ];

  const chart = glassChartTheme(darkMode);
  const muted = 'text-gray-500 dark:text-white/[0.45]';
  const fieldCls = 'rounded-full border border-black/[0.06] bg-white/50 px-4 py-2 text-sm text-gray-700 backdrop-blur-xl focus:outline-none focus:ring-2 focus:ring-[#F80707]/40 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-white/80 dark:[color-scheme:dark]';

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        backTo="/crm/reports"
        backTitle="Back to reports"
        title={<>Client<br />Check Ins</>}
        stats={loading ? [] : overviewTiles}
        statsKey={`${timeRange}-${responseFilter}`}
        controls={(
          <>
            {/* Search + timeframe + response filters (apply to everything below) */}
            <GlassSearch
              className="w-full sm:w-72"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search company, contact, or feedback..."
            />
            <GlassSelect
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              aria-label="Timeframe"
            >
              <option value="thisMonth">This Month</option>
              <option value="month">Pick a Month</option>
              <option value="custom">Date Range</option>
              <option value="last3Months">Last 3 Months</option>
              <option value="last6Months">Last 6 Months</option>
              <option value="lastYear">Last Year</option>
              <option value="allTime">All Time</option>
            </GlassSelect>
            {timeRange === 'month' && (
              <input
                type="month"
                value={monthPick}
                onChange={(e) => setMonthPick(e.target.value)}
                className={fieldCls}
              />
            )}
            {timeRange === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className={fieldCls}
                />
                <span className={`text-sm ${muted}`}>to</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className={fieldCls}
                />
              </div>
            )}
            <SlidingPills
              size="sm"
              options={RESPONSE_OPTIONS}
              value={responseFilter}
              onChange={setResponseFilter}
              ariaLabel="Response status"
            />
          </>
        )}
      />

      {loading ? (
        <div className="flex w-full min-h-[300px] items-center justify-center">
          <BBALoader size={96} />
        </div>
      ) : (
        <div key={`${timeRange}-${responseFilter}`} className="space-y-4">
          {/* Overview: rating distribution chart (KPIs sit in the page header). Raised above the
              table card so the hover panels can overlap it. */}
          <PinnableCard id="rating-overview" title="Check-In Rating Overview" size="wide">
          <GlassCard className="z-10 p-5" delay={0}>
            <GlassCardHeader
              title="Overview"
              right={metrics.noResponse > 0 ? (
                <div
                  className="relative"
                  onMouseEnter={openNoResponseHover}
                  onMouseLeave={scheduleCloseNoResponseHover}
                >
                  <button
                    type="button"
                    onClick={() => setResponseFilter('noResponse')}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#FFB547]/20 px-3 py-1.5 text-xs font-medium text-[#94600A] transition hover:bg-[#FFB547]/30 dark:bg-[#FFB547]/[0.15] dark:text-[#FFB547] dark:hover:bg-[#FFB547]/25"
                  >
                    <Clock className="h-3.5 w-3.5" />
                    {metrics.noResponse} Outstanding
                  </button>
                  {noResponseHoverOpen ? (
                    <div
                      className="absolute right-0 top-full z-50 mt-2 text-left"
                      onMouseEnter={() => { if (noResponseHoverTimer.current) clearTimeout(noResponseHoverTimer.current); }}
                      onMouseLeave={scheduleCloseNoResponseHover}
                    >
                      <CheckInPendingPanel submitters={noResponseSubmitters} darkMode={darkMode} />
                    </div>
                  ) : null}
                </div>
              ) : null}
            />

            {metrics.ratedCount === 0 ? (
              <GlassEmpty className="py-8">
                No ratings for this timeframe yet.
              </GlassEmpty>
            ) : (
              <div className="relative mt-4 w-full h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={barChartRows.filter((r) => r.count > 0)}
                    margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                    barCategoryGap="24%"
                    onMouseMove={(state) => {
                      if (state?.isTooltipActive && state.activePayload?.length) {
                        openChartHover(state.activePayload[0].payload);
                      }
                    }}
                    onMouseLeave={scheduleCloseChartHover}
                  >
                    <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                    <XAxis
                      dataKey="stars"
                      interval={0}
                      height={44}
                      axisLine={false}
                      tickLine={false}
                      tick={(props) => <CheckInStarTick {...props} darkMode={darkMode} />}
                    />
                    <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={40} tick={{ fill: chart.tick, fontSize: 11 }} />
                    <Tooltip
                      content={() => null}
                      cursor={{ fill: chart.cursor }}
                    />
                    <Bar
                      dataKey="count"
                      fill={GLASS_TONE.amber}
                      radius={[12, 12, 12, 12]}
                      maxBarSize={72}
                      background={{ fill: chart.track, radius: 12 }}
                      name="Responses"
                      animationDuration={700}
                    />
                  </BarChart>
                </ResponsiveContainer>

                {chartHover?.row ? (
                  <div
                    className="absolute z-50 left-1/2 -translate-x-1/2"
                    style={{ top: -150 }}
                    onMouseEnter={() => {
                      if (chartHoverTimer.current) clearTimeout(chartHoverTimer.current);
                    }}
                    onMouseLeave={scheduleCloseChartHover}
                  >
                    <CheckInRatingPanel row={chartHover.row} darkMode={darkMode} />
                  </div>
                ) : null}
              </div>
            )}
          </GlassCard>
          </PinnableCard>

          {/* Survey Responses Table */}
          <PinnableCard id="survey-responses" title="Survey Responses" size="wide">
          <GlassCard className="p-5" delay={80}>
            <GlassCardHeader
              title="Survey Responses"
              right={<span className={`text-xs ${muted}`}>({filteredRows.length})</span>}
            />
            {filteredRows.length === 0 ? (
              <GlassEmpty className="py-8">
                {rows.length === 0
                  ? 'No survey results yet. Send a survey to start collecting feedback!'
                  : 'Nothing matches the current filters.'}
              </GlassEmpty>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className={GLASS_TH}>Company</th>
                      <th className={GLASS_TH}>Date</th>
                      <th className={GLASS_TH}>Score</th>
                      <th className={GLASS_TH}>Phone Call</th>
                      <th className={GLASS_TH}>Additional Feedback</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-white/75">
                    {filteredRows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedRow(row)}
                        className={`${GLASS_ROW} cursor-pointer`}
                      >
                        <td className="py-3 pr-4">
                          <ClientCell row={row} />
                        </td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          {row.date ? row.date.toLocaleDateString() : 'Unknown date'}
                        </td>
                        <td className="py-3 pr-4 text-gray-900 dark:text-white">
                          {row.rating != null ? (
                            <div className="flex items-center space-x-1">
                              <span className="font-semibold tabular-nums">{row.rating}/5</span>
                              <StarRow rating={row.rating} />
                            </div>
                          ) : !row.responded ? (
                            <span className="whitespace-nowrap">
                              <GlassBadge tone="amber">Outstanding</GlassBadge>
                            </span>
                          ) : (
                            <span className={muted}>—</span>
                          )}
                        </td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          {row.requestedCall ? (
                            <span className={`inline-flex items-center gap-1.5 font-semibold ${GLASS_TEXT.amber}`}>
                              <Phone className="w-4 h-4" />
                              Yes
                            </span>
                          ) : (
                            <span className={muted}>No</span>
                          )}
                        </td>
                        <td className="py-3">
                          <span className="line-clamp-2">{row.feedback || '-'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </GlassCard>
          </PinnableCard>
        </div>
      )}

      {/* Response Detail Modal (outside the glass cards: backdrop-filter would trap a fixed overlay) */}
      {selectedRow && (
        <div
          className={GLASS_MODAL_OVERLAY}
          onClick={() => setSelectedRow(null)}
        >
          <div
            className={GLASS_MODAL_PANEL}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedRow(null)}
              className="absolute top-4 right-4 grid h-8 w-8 place-items-center rounded-full text-gray-500 transition hover:bg-black/[0.05] hover:text-[#F80707] dark:text-white/50 dark:hover:bg-white/[0.06] dark:hover:text-[#FF4D40]"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex flex-col items-center text-center">
              {selectedRow.imageUrl ? (
                <img
                  src={selectedRow.imageUrl}
                  alt=""
                  className="h-16 w-16 rounded-full object-cover border border-black/10 dark:border-white/[0.15] mb-3"
                />
              ) : (
                <div className="h-16 w-16 rounded-full flex items-center justify-center text-lg font-semibold bg-[#FDD5D0] text-[#1C1916] mb-3">
                  {initialsFromName(selectedRow.companyName)}
                </div>
              )}
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{selectedRow.companyName}</h3>
              {selectedRow.contactName && selectedRow.contactName !== selectedRow.companyName ? (
                <p className={`text-sm ${muted}`}>{selectedRow.contactName}</p>
              ) : null}
              <p className="text-xs mt-1 text-gray-400 dark:text-white/40">
                {selectedRow.date ? selectedRow.date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'Unknown date'}
              </p>

              {selectedRow.rating != null ? (
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xl font-semibold tabular-nums text-gray-900 dark:text-white">{selectedRow.rating}/5</span>
                  <StarRow rating={selectedRow.rating} starClass="text-2xl" />
                </div>
              ) : !selectedRow.responded ? (
                <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#FFB547]/20 px-3 py-1 text-xs font-medium text-[#94600A] dark:bg-[#FFB547]/[0.15] dark:text-[#FFB547]">
                  <Clock className="w-3.5 h-3.5" />
                  Hasn't responded yet
                </span>
              ) : null}

              {selectedRow.feedback ? (
                <p className={`${GLASS_INSET} w-full text-sm italic mt-4 px-4 py-3 break-words whitespace-pre-wrap text-gray-700 dark:text-white/75`}>
                  "{selectedRow.feedback}"
                </p>
              ) : null}

              {selectedRow.requestedCall ? (
                <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#FFB547]/20 px-3 py-1 text-xs font-medium text-[#94600A] dark:bg-[#FFB547]/[0.15] dark:text-[#FFB547]">
                  <Phone className="w-3.5 h-3.5" />
                  Requested a check-in call
                </span>
              ) : null}

              {selectedRow.accountId ? (
                <GlassAccentButton
                  onClick={() => navigate(`/crm/client/${selectedRow.accountId}`)}
                  className="mt-5 w-full justify-center"
                >
                  View Client
                  <ExternalLink className="w-3.5 h-3.5" />
                </GlassAccentButton>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </GlassPage>
  );
};

export default ClientSatisfaction;
